import { fetchAuthSession } from "aws-amplify/auth"

/**
 * A paying user keeps the trial group in the access token until it refreshes
 * (up to an hour), so the backend keeps refusing with TRIAL_EXPIRED. These
 * helpers force a refresh when such a refusal arrives (also TRIAL_STATUS_UNKNOWN,
 * and a limit refusal while an upgrade link is open) and on the return from the
 * upgrade page. A staff move to ExtendedFreeTrial is cured the same way.
 */

/** The Cognito group the Stripe webhook puts a paying user in. */
const PAID_GROUP = "Pilot"
const GROUPS_CLAIM = "cognito:groups"

/** The package groups in the backend's order of precedence (limits.py
 *  PACKAGE_GROUP_PRECEDENCE): the highest one present governs the user. Staff
 *  lift a capped trial user to ExtendedFreeTrial, though nobody paid. */
const PACKAGE_RANK: Record<string, number> = {
  FreeTrial: 1,
  ExtendedFreeTrial: 2,
  [PAID_GROUP]: 3,
}

const packageRank = (groups: readonly string[]): number =>
  Math.max(0, ...groups.map((group) => PACKAGE_RANK[group] ?? 0))

/** Fired once a forced refresh brought a token that may change the account; the
 *  app refetches the user, trial and limits (see useUpgradeSessionSync). */
export const SESSION_REFRESHED_EVENT = "turnus:session-refreshed"

const UPGRADE_LINK_KEY = "turnus-upgrade-link-opened"
/** An access token lives an hour and then refreshes on its own. */
const UPGRADE_LINK_MAX_AGE_MS = 60 * 60 * 1000

/** How long a settled refresh answers later 403s without a new refresh. */
const REFRESH_COOLDOWN_MS = 60 * 1000
/** How long a "not upgraded yet" holds while an upgrade link is open. Shorter:
 *  the payment can reach the token at any moment and the user is waiting for
 *  it, but a burst of rejected writes must not force a Cognito refresh each. */
const PENDING_REFUSAL_COOLDOWN_MS = 15 * 1000

interface SharedRefresh {
  promise: Promise<boolean>
  /** When the answer arrived; null while the refresh is still running. */
  settledAt: number | null
  answer: boolean | null
}

// One shared refresh, not a flag: parallel requests all get the same 403, and
// the later ones must wait for the answer instead of reading "no upgrade".
let trialExpiredRefresh: SharedRefresh | null = null
let trialStatusUnknownRefresh: SharedRefresh | null = null
let upgradeReturnRefresh: Promise<boolean> | null = null
/** When a forced refresh last found the account upgraded or lifted, else null. A
 *  request that left before it carried the old token, however late its refusal
 *  arrives (see refreshAfterTrialBlock). */
let accountChangedAt: number | null = null

/** Forgets the upgrade link flag and every cached refresh answer. Sign-out calls
 *  it so the next user in this tab inherits neither; tests use it between cases. */
export function resetUpgradeSessionState(): void {
  trialExpiredRefresh = null
  trialStatusUnknownRefresh = null
  upgradeReturnRefresh = null
  accountChangedAt = null
  clearUpgradeLink()
}

/** Drops the cached "not upgraded" answers: something happened that they did not
 *  know about, the upgrade link was opened or a refresh found the paid group. */
function forgetRefusals(): void {
  if (trialExpiredRefresh?.answer === false) trialExpiredRefresh = null
  if (trialStatusUnknownRefresh?.answer === false) {
    trialStatusUnknownRefresh = null
  }
}

const stringsOf = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : []

/** The `cognito:groups` claim of an `Authorization` header value ("Bearer ..."),
 *  or null when unreadable: what a request was sent with, to compare a refresh to. */
export function groupsOfBearer(authorization: unknown): string[] | null {
  if (typeof authorization !== "string") return null
  const payload = authorization.replace(/^Bearer\s+/i, "").split(".")[1]
  if (!payload) return null
  try {
    const claims: unknown = JSON.parse(
      atob(payload.replace(/-/g, "+").replace(/_/g, "/")),
    )
    return stringsOf((claims as Record<string, unknown> | null)?.[GROUPS_CLAIM])
  } catch {
    return null
  }
}

/** Forces a refresh; true when the account changed: the paid group, or, given the
 *  groups the request was sent with, a higher trial package (staff lifted the
 *  trial). Without `sentGroups` only payment counts, so the return from the
 *  upgrade page stays about payments. */
async function refreshShowsUpgrade(
  sentGroups: readonly string[] | null = null,
): Promise<boolean> {
  let paid: boolean
  try {
    const session = await fetchAuthSession({ forceRefresh: true })
    const groups = stringsOf(session.tokens?.accessToken?.payload?.[GROUPS_CLAIM])
    paid = groups.includes(PAID_GROUP)
    const lifted =
      sentGroups !== null && packageRank(groups) > packageRank(sentGroups)
    if (!paid && !lifted) return false
  } catch {
    return false
  }
  // Only a payment answers the upgrade link
  if (paid) clearUpgradeLink()
  accountChangedAt = Date.now()
  // A write sent with the old token can still be refused after this: it must
  // not read an earlier "not upgraded" answer
  forgetRefusals()
  window.dispatchEvent(new CustomEvent(SESSION_REFRESHED_EVENT))
  return true
}

/** Forces a token refresh; true when a token came back. Its groups are not
 *  checked: the pre-token trigger rebuilds the claims and may find the trial
 *  clock this time. The upgrade link flag is left alone. */
async function refreshYieldsToken(): Promise<boolean> {
  try {
    const session = await fetchAuthSession({ forceRefresh: true })
    if (!session.tokens?.accessToken) return false
  } catch {
    return false
  }
  window.dispatchEvent(new CustomEvent(SESSION_REFRESHED_EVENT))
  return true
}

/**
 * The refresh behind `current`, or a new one. Callers share a running refresh,
 * and a settled answer holds for a cooldown so a burst of 403s costs one
 * refresh. A "no" holds shorter while an upgrade link is open (the payment can
 * land any moment) and not at all once the link is opened after it.
 */
function sharedRefresh(
  current: SharedRefresh | null,
  run: () => Promise<boolean>,
): SharedRefresh {
  if (current) {
    const { settledAt, answer } = current
    if (settledAt === null) return current
    const cooldown =
      answer === false && isUpgradeLinkPending()
        ? PENDING_REFUSAL_COOLDOWN_MS
        : REFRESH_COOLDOWN_MS
    if (Date.now() - settledAt < cooldown) return current
  }
  const next: SharedRefresh = {
    promise: Promise.resolve(false),
    settledAt: null,
    answer: null,
  }
  next.promise = run().then((answer) => {
    next.settledAt = Date.now()
    next.answer = answer
    return answer
  })
  return next
}

/**
 * For a TRIAL_EXPIRED 403: refresh the session and say whether the account
 * changed since the token was issued: the user upgraded or, with `sentGroups`
 * (the groups the refused request carried), staff moved them to a longer trial
 * package. True means retry the request (the next auth read returns the
 * refreshed token), false means show the trial-ended toast. An answer holds for
 * a minute so a really expired user does not refresh on every later 403.
 */
export function refreshTokenAfterTrialExpired(
  sentGroups: readonly string[] | null = null,
): Promise<boolean> {
  trialExpiredRefresh = sharedRefresh(trialExpiredRefresh, () =>
    refreshShowsUpgrade(sentGroups),
  )
  return trialExpiredRefresh.promise
}

/**
 * For a TRIAL_STATUS_UNKNOWN 403: the token has no trial clock, which the
 * pre-token trigger could not look up. A refresh rebuilds it, so true (a token
 * came back, whatever groups) means send the request again. The middleware
 * refuses before the handler runs, so the retry cannot repeat a write.
 */
export function refreshTokenAfterTrialStatusUnknown(): Promise<boolean> {
  trialStatusUnknownRefresh = sharedRefresh(
    trialStatusUnknownRefresh,
    refreshYieldsToken,
  )
  return trialStatusUnknownRefresh.promise
}

/** What the caller knows about the request that was refused. */
export interface RefusedRequest {
  /** When it left (ms since epoch). */
  startedAt?: number
  /** The groups it was sent with, see `groupsOfBearer`. */
  sentGroups?: readonly string[] | null
}

/** The refresh that can cure a limit error caused by a stale token, picked by
 *  the limit error `kind` of the response; null for every other error.
 *
 *  A usage limit (429) and a missing trial feature (403) are just as stale after
 *  a payment, so they refresh only while an upgrade link is pending: otherwise a
 *  refresh cannot change them and a limit really reached must not cost a Cognito
 *  call. Both are refused before the handler runs (limit_usage and
 *  block_trial_users in api/limits.py), so sending the request again cannot
 *  repeat a write.
 *
 *  A refusal can arrive late: the request left with the old token, the return
 *  from the upgrade page confirmed the upgrade, and only then the 429 or 403
 *  comes back. Such a request (`startedAt` before the confirmation) is sent
 *  again with the current token without asking Cognito once more. */
export function refreshAfterTrialBlock(
  kind: string | undefined,
  request: RefusedRequest = {},
): Promise<boolean> | null {
  const staleTokenKind =
    kind === "trial_expired" ||
    kind === "usage_limit" ||
    kind === "trial_feature_unavailable"
  if (
    staleTokenKind &&
    accountChangedAt !== null &&
    request.startedAt !== undefined &&
    request.startedAt <= accountChangedAt
  ) {
    return Promise.resolve(true)
  }
  if (kind === "trial_expired") {
    return refreshTokenAfterTrialExpired(request.sentGroups)
  }
  if (kind === "trial_status_unknown") {
    return refreshTokenAfterTrialStatusUnknown()
  }
  if (staleTokenKind && isUpgradeLinkPending()) {
    return refreshTokenAfterTrialExpired(request.sentGroups)
  }
  return null
}

/** Remembers that the user went to the upgrade page, for the refresh on return. */
export function markUpgradeLinkOpened(): void {
  // The link is news to a "not upgraded" answer from before it was opened
  forgetRefusals()
  try {
    sessionStorage.setItem(UPGRADE_LINK_KEY, String(Date.now()))
  } catch {
    // storage unavailable: the 403 path still catches a stale token
  }
}

export function isUpgradeLinkPending(): boolean {
  try {
    const openedAt = Number(sessionStorage.getItem(UPGRADE_LINK_KEY))
    return openedAt > 0 && Date.now() - openedAt < UPGRADE_LINK_MAX_AGE_MS
  } catch {
    return false
  }
}

function clearUpgradeLink(): void {
  try {
    sessionStorage.removeItem(UPGRADE_LINK_KEY)
  } catch {
    // nothing stored we could clear
  }
}

/**
 * On return to the app after the upgrade link was opened: force a refresh. The
 * link stays remembered until a token shows the upgrade, because the group
 * reaches Cognito a moment after checkout and the first return can be early.
 */
export function refreshSessionIfUpgradePending(): Promise<void> {
  if (!isUpgradeLinkPending()) return Promise.resolve()
  return refreshForPaidAccess().then(() => undefined)
}

/**
 * After a payment: true once a forced refresh shows the paid group. Asks the
 * token, not the link flag: storage can refuse the flag, and an absent flag
 * proves nothing about the plan.
 */
export function refreshForPaidAccess(): Promise<boolean> {
  upgradeReturnRefresh ??= refreshShowsUpgrade().finally(() => {
    upgradeReturnRefresh = null
  })
  return upgradeReturnRefresh
}
